interface Certificate {
    name: string;
    description: string;
    logo: string;
}

export const getEnvironmentalCertificate = (key: string): Certificate[] => {
    const certificateTypes = certificateMappings[key] || [];
    return certificateTypes.sort((type1, type2) => type1 - type2).map((type) => certificates[type]);
};

enum CertificateType {
    ECO_LIGHTHOUSE = 0,
    SWAN_LABEL = 1,
    GREEN_KEY = 2,
    ISO_14001 = 3,
    ECOTOURISM_NORWAY = 4,
}

const certificates: Record<CertificateType, Certificate> = {
    [CertificateType.ECO_LIGHTHOUSE]: {
        name: 'Miljøfyrtårn',
        description:
            'Miljøfyrtårn-bedrifter jobber mot å tilfredsstille krav og implementere miljøtiltak på en systematisk og kontinuerlig måte ' +
            'for å skape en mer miljøvennlig drift og et tryggere arbeidsmiljø. Sertifiserte bedrifter er underlagt en uavhengig ' +
            'vurdering og må gjennomgå en sertifiseringsprosess hvert tredje år.',
        logo: require('../../../../../../../public/images/certificates/eco-lighthouse.png').default?.src,
    },
    [CertificateType.SWAN_LABEL]: {
        name: 'Svanemerket',
        description:
            'Mer enn 5000 produkter og tjenester i Norge er sertifisert med Svanemerket, som indikerer at de tilfredsstiller strenge ' +
            'krav innen energieffektivitet, bruk av miljøvennlige materialer og kjemikalier. Helt fra råvarer til sluttprodukt og ' +
            'avfallshåndtering.',
        logo: require('../../../../../../../public/images/certificates/swan-label.png').default?.src,
    },
    [CertificateType.GREEN_KEY]: {
        name: 'Green Key',
        description:
            'Green Key er et globalt miljømerke for hoteller, overnattingssteder, campingplasser og attraksjoner. Sertifiserte ' +
            'virksomheter må oppfylle strenge kriterier for avfallshåndtering, energi-, vannforbruk, anskaffelser, grøntområder, ' +
            'samfunnsansvar og involvering av ansatte. Høye standarder opprettholdes gjennom en årlig sertifikatfornyelse, ' +
            'streng søknadsprosess, dokumentasjonskrav og hyppige revisjoner.',
        logo: require('../../../../../../../public/images/certificates/green-key.png').default?.src,
    },
    [CertificateType.ISO_14001]: {
        name: 'ISO-14001',
        description:
            'ISO 14001 sertifiseringen gis til bedrifter som har systemer av høy kvalitet og bidrar til gode effekter for miljøet.',
        logo: require('../../../../../../../public/images/certificates/iso-14001.png').default?.src,
    },
    [CertificateType.ECOTOURISM_NORWAY]: {
        name: 'Ecotourism Norway',
        description:
            'Denne nasjonale sertifiseringen tildeles bedrifter og operatører som holder et høyt internasjonalt nivå innen økoturisme. ' +
            'Mer enn 100 strenge kriterier innen miljøprestasjoner, vertskap, integrering av lokalsamfunn og innkjøp må oppfylles og ' +
            'ofte forbedres. Sertifikatet fornyes hvert tredje år.',
        logo: require('../../../../../../../public/images/certificates/ecotourism-norway.png').default?.src,
    },
};

const certificateMappings: Record<string, CertificateType[]> = {
    Thon_Hotel_Polar: [CertificateType.ECO_LIGHTHOUSE],
    Comfort_Hotel_Xpress_Tromso: [CertificateType.ISO_14001],
    Fru_Haugans_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Sure_Hotel_by_Best_Western_Hotel_Ole_Tobias: [CertificateType.ISO_14001],
    Henningsvaer_Bryggehotel_by_Classic_Norway: [CertificateType.ECO_LIGHTHOUSE],
    Skagen_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Clarion_Collection_Hotel_Grand_Bodo: [CertificateType.ISO_14001],
    Rognan_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Nordlys: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Havet: [CertificateType.SWAN_LABEL],
    Scandic_Kirkenes: [CertificateType.SWAN_LABEL],
    Scandic_Bryggen: [CertificateType.SWAN_LABEL],
    Scandic_Karasjok: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Kirkenes: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Andrikken: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Alta: [CertificateType.SWAN_LABEL],
    Quality_Hotel_Grand_Royal: [CertificateType.ISO_14001],
    Clarion_Collection_Hotel_Aurora: [CertificateType.ISO_14001],
    Scandic_Svolvaer: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Alta: [CertificateType.ECO_LIGHTHOUSE],
    Clarion_Collection_Hotel_With: [CertificateType.ISO_14001],
    Radisson_Blu_Hotel_Tromso: [CertificateType.GREEN_KEY],
    Scandic_Meyergarden: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Bronnoysund: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Bodo: [CertificateType.SWAN_LABEL],
    Clarion_Hotel_The_Edge: [CertificateType.ISO_14001],
    Quality_Hotel_Saga: [CertificateType.ISO_14001],
    Scandic_Grand_Tromso: [CertificateType.SWAN_LABEL],
    Scandic_Ishavshotel: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Tromso: [CertificateType.ECO_LIGHTHOUSE],
    Valldal_Fjordhotell_By_Classic_Norway_Hotels: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Otta: [CertificateType.ECO_LIGHTHOUSE],
    Radisson_Blu_Mountain_Resort_Residences_Trysil: [CertificateType.ECO_LIGHTHOUSE],
    Best_Western_Leto_Arena: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Bergen_City: [CertificateType.SWAN_LABEL],
    Grand_Hotel_Terminus: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Bergen_Airport: [CertificateType.ECO_LIGHTHOUSE],
    Clarion_Hotel_Admiral: [CertificateType.ISO_14001],
    Thon_Hotel_Bristol_Oslo: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Orion: [CertificateType.ECO_LIGHTHOUSE],
    Hotel_Park_Bergen: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Rosenkrantz: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Kokstad: [CertificateType.SWAN_LABEL],
    Quality_Hotel_Edvard_Grieg: [CertificateType.ISO_14001],
    Clarion_Collection_Hotel_Havnekontoret: [CertificateType.ISO_14001],
    First_Hotel_Atlantica: [CertificateType.ECO_LIGHTHOUSE],
    Hotel_Norge_by_Scandic: [CertificateType.ECO_LIGHTHOUSE, CertificateType.SWAN_LABEL],
    Quality_Hotel_Waterfront_Alesund: [CertificateType.ISO_14001],
    Thon_Hotel_Fosnavag: [CertificateType.ECO_LIGHTHOUSE],
    Thorbjornrud_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Saga: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Sandnes: [CertificateType.ECO_LIGHTHOUSE],
    Hotell_Jaeren: [CertificateType.ECO_LIGHTHOUSE],
    Comfort_Hotel_Stavanger: [CertificateType.ISO_14001],
    Best_Western_Havly_Hotell: [CertificateType.SWAN_LABEL, CertificateType.ISO_14001],
    Scandic_Stavanger_Park: [CertificateType.SWAN_LABEL],
    Quality_Hotel_Stavanger_Airport: [CertificateType.ISO_14001],
    Scandic_Stavanger_Airport: [CertificateType.SWAN_LABEL],
    Scandic_Stavanger_Forus: [CertificateType.SWAN_LABEL],
    Scandic_Stavanger_City: [CertificateType.SWAN_LABEL],
    Comfort_Hotel_Floro: [CertificateType.ISO_14001],
    Clarion_Collection_Hotel_Hammer: [CertificateType.ISO_14001],
    Thon_Hotel_Skeikampen: [CertificateType.ECO_LIGHTHOUSE],
    Best_Western_Laegreid_Hotell: [CertificateType.ECO_LIGHTHOUSE],
    Jostedal_Camping: [CertificateType.GREEN_KEY],
    Scandic_Neptun: [CertificateType.SWAN_LABEL],
    Panorama_Hotell_Resort: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Ornen: [CertificateType.SWAN_LABEL],
    Grand_Hotel_Egersund: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Stavanger: [CertificateType.ECO_LIGHTHOUSE],
    Fjaerland_Fjordstue_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    '292_Aurland': [CertificateType.ECO_LIGHTHOUSE],
    First_Hotel_Victoria: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Hamar: [CertificateType.SWAN_LABEL],
    Lillesand_Hotel_Norge: [CertificateType.ECO_LIGHTHOUSE],
    Erzscheidergaarden: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Halden: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Harstad: [CertificateType.ECO_LIGHTHOUSE],
    Lampeland_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Grimstad: [CertificateType.SWAN_LABEL],
    Clarion_Collection_Hotel_Arcticus: [CertificateType.ISO_14001],
    Thon_Hotel_Spectrum: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Terminus: [CertificateType.ECO_LIGHTHOUSE],
    Radisson_Blu_Plaza_Hotel_Oslo: [CertificateType.GREEN_KEY],
    Thon_Hotel_Opera: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Tonsberg_Brygge: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Cecil: [CertificateType.ECO_LIGHTHOUSE],
    Grand_Hotel_Asgardstrand: [CertificateType.ECO_LIGHTHOUSE],
    Losby_Gods_Manor: [CertificateType.ECO_LIGHTHOUSE],
    Zander_K_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Bo_Hotel_Bo: [CertificateType.ECO_LIGHTHOUSE],
    Skarsnuten_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Pond: [CertificateType.ISO_14001],
    Thon_Hotel_Munch: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Parken: [CertificateType.ECO_LIGHTHOUSE],
    Cochs_Pensjonat: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Byporten: [CertificateType.SWAN_LABEL],
    Scandic_Solli: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Vika_Atrium: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Slottsparken: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Gyldenlove: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Astoria: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Oslo_City: [CertificateType.SWAN_LABEL],
    First_Hotel_Millennium: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Holberg: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Arena: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Linne: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Holmenkollen_Park: [CertificateType.SWAN_LABEL],
    Radisson_Blu_Hotel_Nydalen_Oslo: [CertificateType.GREEN_KEY],
    Scandic_Victoria: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Oslo_Panorama: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Ullevaal_Stadion: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Expo: [CertificateType.ISO_14001],
    Scandic_Fornebu: [CertificateType.SWAN_LABEL],
    Jegtvolden_Fjordhotell: [CertificateType.ECO_LIGHTHOUSE],
    Son_Spa: [CertificateType.ISO_14001],
    Ustedalen_Hotel_Hol: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Moldefjord: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Arendal: [CertificateType.ECO_LIGHTHOUSE],
    Bergen_Bors_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Kongsvinger_Castle_Hotel_Resort: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Flesland_Airport: [CertificateType.SWAN_LABEL],
    Svalbard_Hotell_The_Vault: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Grand_Steinkjer: [CertificateType.ISO_14001],
    Hotel_Refsnes_Gods: [CertificateType.ECO_LIGHTHOUSE],
    Augustin_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Skaergaarden: [CertificateType.ISO_14001],
    Hotel_Vic: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Hoyers: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Parken: [CertificateType.SWAN_LABEL],
    Hotel_Noreg: [CertificateType.SWAN_LABEL],
    Hotel_Union_Oye: [CertificateType.ECO_LIGHTHOUSE],
    Runde_Miljosenter: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Strand: [CertificateType.ISO_14001],
    Honne_Hotell_Konferansesenter: [CertificateType.GREEN_KEY],
    Klaekken_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Maritim: [CertificateType.SWAN_LABEL],
    GamlaVaerket_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Residence: [CertificateType.ISO_14001],
    Hotel_Sverre: [CertificateType.ECO_LIGHTHOUSE],
    Radisson_Blu_Atlantic_Hotel_Stavanger: [CertificateType.GREEN_KEY],
    Victoria_Hotel_Stavanger: [CertificateType.ECO_LIGHTHOUSE, CertificateType.SWAN_LABEL],
    Scandic_Forum: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Maritim: [CertificateType.ECO_LIGHTHOUSE],
    St_Svithun_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Energihotellet: [CertificateType.ECO_LIGHTHOUSE],
    Bekkjarvik_Gjestgiveri: [CertificateType.ECO_LIGHTHOUSE],
    Bomlo_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Birkebeineren_Hotel_Apartments: [CertificateType.ECO_LIGHTHOUSE],
    Lillehammer_Stasjonen_Hotel_Hostel: [CertificateType.GREEN_KEY],
    TOP_Molla_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Lillehammer_Hotel: [CertificateType.SWAN_LABEL],
    Scandic_Hafjell: [CertificateType.SWAN_LABEL],
    Hafjell_Resort_Jaertunet: [CertificateType.ECO_LIGHTHOUSE],
    Storefjell_Resort_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Valdres: [CertificateType.SWAN_LABEL],
    Dalen_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Kviknes_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Sogndal: [CertificateType.ISO_14001],
    Bergstadens_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Flamsbrygga_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Lindstroem_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Jolster: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Sunnfjord_Hotel_Spa: [CertificateType.SWAN_LABEL],
    Scandic_Royal_Stavanger: [CertificateType.GREEN_KEY],
    Grand_Hotel_Oslo_by_Scandic: [CertificateType.SWAN_LABEL],
    TrollAktiv_Adventure_Park_Evje: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Park_Sandefjord: [CertificateType.SWAN_LABEL],
    Roros_Hotell_Bad_Velvaere: [CertificateType.ECO_LIGHTHOUSE],
    Basecamp_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Vertshuset_Roros: [CertificateType.ECO_LIGHTHOUSE],
    Bardola_Hoyfjellshotell: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Kristiansund: [CertificateType.ECO_LIGHTHOUSE],
    Lysebu_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Farsund_Resort: [CertificateType.SWAN_LABEL],
    Tregde_Ferie: [CertificateType.ECO_LIGHTHOUSE],
    Knutholmen: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Sorlandet: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Norge: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Kristiansand_Bystranda: [CertificateType.SWAN_LABEL],
    Hallingdal_Feriepark: [CertificateType.ECO_LIGHTHOUSE],
    Hustadvika_Guesthouse_By_Classic_Norway_Hotels: [CertificateType.ECO_LIGHTHOUSE],
    Skaslien_Hotel_Guesthouse: [CertificateType.ECO_LIGHTHOUSE],
    Dolen_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Best_Western_Tingvold_Park_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Hotel_Continental_Oslo: [CertificateType.ECO_LIGHTHOUSE],
    Solvgarden_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Backlund: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Vulkan: [CertificateType.SWAN_LABEL],
    Sundvolden_Hotel: [CertificateType.SWAN_LABEL],
    Radisson_Blu_Scandinavia_Hotel_Oslo: [CertificateType.GREEN_KEY],
    Scandic_St_Olavs_Plass: [CertificateType.SWAN_LABEL],
    Molde_Fjordstuer_by_Classic_Norway: [CertificateType.ECO_LIGHTHOUSE],
    Haholmen_Havstuer_by_Classic_Norway: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Alexandra_Molde: [CertificateType.ISO_14001],
    Scandic_Seilet: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Kristiansand: [CertificateType.ECO_LIGHTHOUSE],
    Langedrag_Naturpark: [CertificateType.ECO_LIGHTHOUSE],
    Engo_Gard_Hotel_Restaurant: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Rock_City: [CertificateType.SWAN_LABEL],
    Vauldalen: [CertificateType.ECO_LIGHTHOUSE],
    Hotel_Klubben: [CertificateType.ISO_14001],
    Holmen_Fjordhotell: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Leangkollen: [CertificateType.ISO_14001],
    Scandic_Asker: [CertificateType.SWAN_LABEL],
    Quality_Hotel_Entry: [CertificateType.ISO_14001],
    Scandic_Gardermoen: [CertificateType.SWAN_LABEL],
    Quality_Hotel_Fredrikstad: [CertificateType.ISO_14001],
    Quality_Hotel_Olavsgaard: [CertificateType.ISO_14001],
    Quality_Hotel_Sarpsborg: [CertificateType.ISO_14001],
    Quality_Hotel_Grand_Farris: [CertificateType.ISO_14001],
    Mandal_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Kragero_Resort: [CertificateType.ECO_LIGHTHOUSE],
    Strand_Hotel_Fevik_By_Classic_Norway_Hotels: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Ulstein: [CertificateType.ISO_14001],
    Vestlia_Resort: [CertificateType.ECO_LIGHTHOUSE],
    Revsnes_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Ona_Havstuer_By_Classic_Norway_Hotels: [CertificateType.ECO_LIGHTHOUSE],
    Finnoy_Havstuer_By_Classic_Norway_Hotels: [CertificateType.ECO_LIGHTHOUSE],
    Roisheim_Hotel_Skysstasjon: [CertificateType.ECO_LIGHTHOUSE],
    Smarthotel_Forus: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Storo: [CertificateType.ECO_LIGHTHOUSE],
    Holmvik_Brygge_Nyksund: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Alesund: [CertificateType.ISO_14001],
    Visnes_Hotel_Stryn: [CertificateType.ECO_LIGHTHOUSE],
    Innvik_Fjordhotell: [CertificateType.ECO_LIGHTHOUSE],
    Sure_Hotel_by_Best_Western_Harstad_Narvik_Airport: [CertificateType.ECO_LIGHTHOUSE],
    Thon_Hotel_Bjorneparken: [CertificateType.ECO_LIGHTHOUSE],
    Solstrand_Hotel_Bad: [CertificateType.ECO_LIGHTHOUSE],
    Sola_Strand_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Kronen_Gaard_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Stord_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Pellestova_Hotell_Hafjell: [CertificateType.ECO_LIGHTHOUSE],
    Clarion_Hotel_The_Hub: [CertificateType.ISO_14001],
    Hurdalsjoen_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Radisson_Blu_Caledonien_Hotel_Kristiansand: [CertificateType.GREEN_KEY],
    Thon_Hotel_Horten: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Sjolyst: [CertificateType.SWAN_LABEL],
    Thon_Hotel_Hallingdal: [CertificateType.ECO_LIGHTHOUSE],
    Kjobmandsgaarden_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Scandic_Havna_Tjome: [CertificateType.SWAN_LABEL],
    Hotel_Union_Geiranger_Bad_Spa: [CertificateType.ECO_LIGHTHOUSE],
    Hotel_Geiranger: [CertificateType.ECO_LIGHTHOUSE],
    Rederiet_Hotel: [CertificateType.ECO_LIGHTHOUSE],
    Quality_Hotel_Floro: [CertificateType.ISO_14001],
    Kirkvollen_pilegrimsgard: [CertificateType.ECOTOURISM_NORWAY],
};
